import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import businessSetupHero from "@/assets/hero/hero-business-setup.jpg";

export const Route = createFileRoute("/business-setup/")({
  head: () =>
    buildHead({
      title: "Business Setup for Real Estate Investors | LLC, Bank, ITIN",
      description:
        "Set up the LLC, business bank account, EIN and ITIN you need to close a business-purpose investment property loan in an entity.",
      url: "/business-setup",
      keywords:
        "business setup for real estate investors, LLC for rental property, business bank account for investors, ITIN for foreign national investors, DUNS number for LLC, business credit for investors, entity vesting mortgage, landlord insurance for LLC, property management software for landlords, title services for investors, EIN for rental property LLC",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
      ],
      service: { name: "Business Setup for Investment Property" },
    }),
  component: BusinessSetupHub,
});

const CARDS = [
  { to: "/business-setup/llc-journey", title: "Buying in an LLC: step by step", body: "The full journey in order — form the LLC and EIN, add an ITIN if you are non-US, open business banking, register a DUNS number, then finance, close, and operate the property." },
  { to: "/business-setup/llc", title: "Set up your LLC", body: "Our loans are vested in an LLC, LP, or corporation. Form the entity first so title, insurance, and the loan documents all line up at closing." },
  { to: "/business-setup/business-bank-account", title: "Open a business bank account", body: "Reserves, rent deposits, and closing funds should move through an account in the entity's name. Underwriting reviews these statements." },
  { to: "/business-setup/business-loans", title: "Business loans & business credit", body: "Working capital under the entity for rehab carry and operations, and a credit file that is separate from your personal profile." },
  { to: "/business-setup/duns-number", title: "Get a DUNS number", body: "The identifier most business credit reporting is tied to. Register it early so your LLC starts building its own history." },
  { to: "/business-setup/landlord-insurance", title: "Landlord insurance", body: "Investor policies with loss of rents, liability, and the mortgagee clause every closing requires — including short-term rental coverage." },
  { to: "/business-setup/property-management-software", title: "Property management software", body: "Rent collection, leases, maintenance, and owner reporting kept in one place, ready for your next refinance or portfolio loan." },
  { to: "/business-setup/itin", title: "ITIN for non-US clients", body: "Foreign national investors without a Social Security number generally need an ITIN for tax filings and for many banking relationships." },
  { to: "/business-setup/title-services", title: "Title & escrow services", body: "Title search, commitment, escrow, and closing coordination for purchases and refinances vested in an entity." },
];

function BusinessSetupHub() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Get your entity, banking, and tax IDs in place"
        subtitle="Business-purpose investment loans close in an entity. Here is what to line up before you apply — and the partners we work with to get it done quickly."
        image={{
          src: businessSetupHero,
          alt: "Investor at a home desk organizing LLC documents and business banking paperwork with the Simply Approved Mortgages website open on a laptop",
        }}
      />
      <PageEssentials />
      <Section>
        <SectionHeader eyebrow="Start here" title="What to line up before closing" />
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((c) => (
            <Link key={c.to} to={c.to} className="rounded-xl border border-border bg-card p-6 transition-colors hover:border-brand">
              <div className="font-display text-lg font-bold text-brand">{c.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
              <div className="mt-4 text-sm font-semibold text-brand">Learn more →</div>
            </Link>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/partners" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">See all partners</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
